import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect, SearchableSelectProps } from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { TimeTrackingConfigurationUtils } from '../../../classes/entities/TimeTrackingConfiguration/TimeTrackingConfigurationUtils';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useNullableSelectEntityItems } from '../../../hooks/entity/useNullableSelectEntityItems/useNullableSelectEntityItems';
import { UseSelectEntityItemsUtils } from '../../../hooks/entity/useSelectEntityItems/UseSelectEntityItemsUtils';
export type TimeTrackingConfigurationSelectProps = PropsWithoutChildren<Omit<Partial<SearchableSelectProps<never>>, 'getKeyOfItem' | 'getDisplayTextOfItem' | 'items' | 'value' | 'onValueChanged'>> & {
  value: string | null;
  onValueChanged: (value: string | null) => void;
  userGroupId: string;
};
export const TimeTrackingConfigurationSelect: FunctionComponent<TimeTrackingConfigurationSelectProps> = React.memo(props => {
  const $ = _c(29);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let onValueChanged;
  let remainingProps;
  let userGroupId;
  let value;
  if ($[2] !== t0) {
    ({
      userGroupId,
      value,
      onValueChanged,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = onValueChanged;
    $[4] = remainingProps;
    $[5] = userGroupId;
    $[6] = value;
  } else {
    onValueChanged = $[3];
    remainingProps = $[4];
    userGroupId = $[5];
    value = $[6];
  }
  const {
    t
  } = useTranslation();
  const timeTrackingConfigurations = useEntities(EntityName.TimeTrackingConfiguration, "getByUserGroupId", userGroupId);
  let t1;
  if ($[7] !== timeTrackingConfigurations) {
    t1 = [null, ...timeTrackingConfigurations];
    $[7] = timeTrackingConfigurations;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const timeTrackingConfigurationsWithNullValue = t1;
  let t2;
  if ($[9] !== t) {
    t2 = t("components.timeTrackingConfiguration.TimeTrackingConfigurationSelect.notFound");
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t) {
    t3 = t("components.timeTrackingConfiguration.TimeTrackingConfigurationSelect.nullValue");
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== onValueChanged || $[14] !== t2 || $[15] !== t3 || $[16] !== timeTrackingConfigurationsWithNullValue || $[17] !== value) {
    t4 = {
      value,
      entities: timeTrackingConfigurationsWithNullValue,
      entityNotFoundText: t2,
      nullValueText: t3,
      getDisplayTextOfEntity: TimeTrackingConfigurationUtils.displayTextGetter,
      getKeyOfEntity: TimeTrackingConfigurationUtils.keyGetter,
      getValueOfEntity: UseSelectEntityItemsUtils.entityIdGetter,
      onValueChanged
    };
    $[13] = onValueChanged;
    $[14] = t2;
    $[15] = t3;
    $[16] = timeTrackingConfigurationsWithNullValue;
    $[17] = value;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const useSelectEntityItemsReturnValue = useNullableSelectEntityItems(t4);
  let t5;
  if ($[19] !== t) {
    t5 = t("components.timeTrackingConfiguration.TimeTrackingConfigurationSelect.label");
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== remainingProps || $[22] !== t5 || $[23] !== useSelectEntityItemsReturnValue.getDisplayTextOfItem || $[24] !== useSelectEntityItemsReturnValue.getKeyOfItem || $[25] !== useSelectEntityItemsReturnValue.items || $[26] !== useSelectEntityItemsReturnValue.onSelectedItemChanged || $[27] !== useSelectEntityItemsReturnValue.selectedItem) {
    t6 = <SearchableSelect label={t5} getDisplayTextOfItem={useSelectEntityItemsReturnValue.getDisplayTextOfItem} getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem} items={useSelectEntityItemsReturnValue.items} value={useSelectEntityItemsReturnValue.selectedItem} onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged} {...remainingProps} />;
    $[21] = remainingProps;
    $[22] = t5;
    $[23] = useSelectEntityItemsReturnValue.getDisplayTextOfItem;
    $[24] = useSelectEntityItemsReturnValue.getKeyOfItem;
    $[25] = useSelectEntityItemsReturnValue.items;
    $[26] = useSelectEntityItemsReturnValue.onSelectedItemChanged;
    $[27] = useSelectEntityItemsReturnValue.selectedItem;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
});